import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { find } from "lodash"

import { LOADING_INITIAL_STATE, isTimeout, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"

// ACTIONS
const requested = createAction("communication/requested")
const received = createAction("communication/received")
const failed = createAction("communication/failed")

const updatingMessage = createAction("communication/updatingMessage")
const updatedMessage = createAction("communication/updatedMessage")

const updatingContent = createAction("communication/updatingContent")
const updatedContent = createAction("communication/updatedContent")

const fetchingContent = createAction("communication/fetchingContent")
const fetchedContent = createAction("communication/fetchedContent")

// REDUCER
const initialState = {
  ...LOADING_INITIAL_STATE,
  marketplace: [],
  manual: [],
  updatingId: null,
  updatingContentId: null,
  fetchingContentId: null,
  contentById: {}
}

const communicationReducer = createReducer(initialState, {
  [requested.type]: (state, action) => {
    if (isTimeout(state)) { action.abort = true; return }
    setRequested(state)
  },
  [received.type]: (state, action) => {
    setReceived(state)
    const payload = action.payload?.data || action.payload || {}
    const normalized = Object.entries(payload).flatMap(([category, items]) =>
      Array.isArray(items) ? items.map((item) => ({
        id: item.id,
        section: category.charAt(0).toUpperCase() + category.slice(1),
        type: item.mode,
        name: item.label,
        trigger_event: item.trigger_event,
        enabled: item.enabled,
        disabled: !item.enabled,
        is_editable: !!item.is_editable,
        subject: item.subject || "",
        body: item.body || "",
        message_body: item.message_body || "",
        footer: item.footer || ""
      })) : []
    )
    state.marketplace = normalized
    state.manual = normalized.map((item) => ({ ...item }))
  },
  [failed.type]: (state) => {
    setFailed(state)
    state.updatingId = null
    state.updatingContentId = null
  },
  [updatingMessage.type]: (state, action) => {
    state.updatingId = action.props.id
  },
  [updatedMessage.type]: (state, action) => {
    setReceived(state)
    state.updatingId = null
    const { id, enabled, is_editable } = action.props
    ;[state.marketplace, state.manual].forEach((list) => {
      const item = find(list, ["id", id])
      if (item) {
        item.enabled = enabled
        item.disabled = !enabled
        if (is_editable !== undefined) item.is_editable = is_editable
      }
    })
  },
  [updatingContent.type]: (state, action) => {
    state.updatingContentId = action.props.id
  },
  [updatedContent.type]: (state, action) => {
    state.updatingContentId = null
    const { id, message_body, footer, body } = action.props
    ;[state.marketplace, state.manual].forEach((list) => {
      const item = find(list, ["id", id])
      if (item) {
        item.message_body = message_body
        item.footer = footer
        if (body) item.body = body
      }
    })
  },
  [fetchingContent.type]: (state, action) => {
    state.fetchingContentId = action.props.id
  },
  [fetchedContent.type]: (state, action) => {
    state.fetchingContentId = null
    const { id, message_body, footer } = action.props
    state.contentById[id] = { message_body, footer }
  }
})

export default communicationReducer

// THUNKS
export const fetchCommunicationMessages = (bookingSource) => (dispatch) =>
  dispatch(
    api({
      url: "/guide/settings/communication",
      params: bookingSource ? { booking_source: bookingSource } : undefined,
      onStart: requested.type,
      onSuccess: received.type,
      onError: failed.type
    })
  )

export const updateManualMessage = (id, enabled) => async (dispatch) => {
  dispatch({ type: updatingMessage.type, props: { id } })
  try {
    const data = await dispatch(
      api({
        url: `/guide/settings/communication/${id}`,
        method: "patch",
        data: { communication: { enabled } },
        onError: failed.type,
        showSuccessNotification: true
      })
    )
    const payload = data?.data || data || {}
    dispatch({
      type: updatedMessage.type,
      props: {
        id,
        enabled: payload.enabled ?? enabled,
        is_editable: payload.is_editable
      }
    })
  } catch {
    // onError already dispatched by api helper
  }
}

const sanitizeHtml = (val) => {
  const stripped = (val || "").replace(/<br\s*\/?>/gi, "").replace(/<[^>]+>/g, "").trim()
  return stripped ? val : ""
}

export const updateTemplateContent = (id, message_body, footer) => async (dispatch) => {
  dispatch({ type: updatingContent.type, props: { id } })
  const cleanBody = sanitizeHtml(message_body)
  const cleanFooter = sanitizeHtml(footer)
  try {
    const data = await dispatch(
      api({
        url: `/guide/settings/communication/${id}/update_content`,
        method: "patch",
        data: { communication: { message_body: cleanBody, footer: cleanFooter } },
        onError: failed.type,
        showSuccessNotification: true
      })
    )
    const payload = data?.communication || data?.data || data || {}
    dispatch({
      type: updatedContent.type,
      props: {
        id,
        message_body: payload.message_body ?? message_body,
        footer: payload.footer ?? footer,
        body: payload.body
      }
    })
  } catch {
    // onError already dispatched by api helper
  }
}

export const fetchTemplateContent = (id) => async (dispatch) => {
  dispatch({ type: fetchingContent.type, props: { id } })
  try {
    const data = await dispatch(
      api({
        url: `/guide/settings/communication/${id}/content`,
        onError: failed.type
      })
    )
    const payload = data?.communication || data?.data || data || {}
    dispatch({
      type: fetchedContent.type,
      props: {
        id,
        message_body: payload.message_body || "",
        footer: payload.footer || ""
      }
    })
  } catch {
    // onError already dispatched by api helper
  }
}

// SELECTORS
export const communicationSelector = (state) => state.communication
export const marketplaceMessagesSelector = (state) => state.communication.marketplace
export const manualMessagesSelector = (state) => state.communication.manual
export const communicationLoadingSelector = (state) => state.communication.loading
export const communicationUpdatingIdSelector = (state) => state.communication.updatingId
export const communicationUpdatingContentIdSelector = (state) => state.communication.updatingContentId
export const communicationFetchingContentIdSelector = (state) => state.communication.fetchingContentId
export const communicationContentByIdSelector = (state) => state.communication.contentById
